/**
 * Taking money off one line of a sale.
 *
 * The cart arithmetic has always handled this — `computeLine` subtracts a
 * line's discount before the order-level one is worked out, and `order_lines`
 * has carried `discount_amount` since the schema was written — but nothing at
 * the till could set it, so the only discount a cashier could give was against
 * the whole sale. Day-old bread at half price meant doing the sum by hand and
 * keying an open item.
 */

import { useState } from 'react';
import { formatMoney, percentOf, type CartLineTotals, type Discount } from '@bakery/shared';

import { Button, Field, Modal, MoneyInput, cx } from '../components/ui.js';

export function LineDiscountModal({
  line,
  discounts,
  onClose,
  onApply,
}: {
  line: CartLineTotals;
  discounts: Discount[];
  onClose: () => void;
  onApply: (centavos: number) => void;
}) {
  const [custom, setCustom] = useState(line.discountAmount);

  /** What a discount is worth against this line, in centavos. */
  const worth = (discount: Discount) =>
    discount.type === 'percent' ? percentOf(line.grossTotal, discount.value) : discount.value;

  /*
   * A discount can never exceed the line it is applied to. `computeLine` clamps
   * it anyway, so this is about what the cashier is shown rather than about
   * safety — an amount-off discount bigger than the item should read as "the
   * whole line", not as a number that silently means something else.
   */
  const clamp = (centavos: number) => Math.min(Math.max(centavos, 0), line.grossTotal);

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Discount ${line.name}`}
      description={`${line.qty} × ${formatMoney(line.effectiveUnitPrice)} — ${formatMoney(line.grossTotal)} before any discount`}
      footer={
        <>
          {line.discountAmount > 0 && (
            <Button variant="ghost" className="mr-auto" onClick={() => onApply(0)}>
              Remove discount
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onApply(clamp(custom))}>
            Take off {formatMoney(clamp(custom))}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {discounts.length > 0 && (
          <div className="space-y-1.5">
            {discounts.map((discount) => {
              const amount = clamp(worth(discount));
              const chosen = custom === amount && amount > 0;

              return (
                <button
                  key={discount.id}
                  type="button"
                  onClick={() => setCustom(amount)}
                  className={cx(
                    'flex w-full items-center justify-between gap-3 rounded-[var(--radius-md)] border px-3 py-2 text-left text-sm transition',
                    chosen
                      ? 'border-crust-600 bg-[var(--color-accent-soft)]'
                      : 'border-[var(--color-line)] hover:bg-[var(--color-surface-sunken)]',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {discount.name}
                    <span className="ml-2 text-xs text-[var(--color-ink-faint)]">
                      {discount.type === 'percent' ? `${discount.value}%` : formatMoney(discount.value)}
                    </span>
                  </span>
                  <span className="tabular shrink-0 font-medium text-[var(--color-ok)]">−{formatMoney(amount)}</span>
                </button>
              );
            })}
          </div>
        )}

        {/*
          * The open amount stays available whichever discounts exist. Half a
          * tray of yesterday's bread is not a policy anybody sets up in
          * Settings first, and a cashier who cannot key a number simply rings
          * the item up wrong instead.
          */}
        <Field
          label="Or an amount off this line"
          hint={`At most ${formatMoney(line.grossTotal)}, which is what this line comes to.`}
        >
          <MoneyInput value={custom} onChange={(centavos) => setCustom(clamp(centavos))} />
        </Field>
      </div>
    </Modal>
  );
}
